import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeviceCreate } from '../../../api/models';
import { clientService } from '../../../services/clientService';
import { clientPathLabel, indexClients } from '../../../utils/clientTree';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { isAbortError } from '../../../utils/abortError';
import { DeviceFilters, DevicePage, DeviceSortField, fetchDevicePage } from './deviceQuery';

export interface DevicesListOptions {
  refreshTrigger?: number;
  initialDeviceTypeFilter?: string;
  /** 'active' or 'refresh': the status filter to start with (dashboard links). */
  initialStatusFilter?: string;
  /** Client ids to start filtered on (dashboard links). */
  initialClientFilter?: string[];
}

export interface ClientOption { id: string; name: string }

/**
 * The device list: its filters, sort and page, the page the API returned,
 * and the client names for the client column. Filters, sort and search
 * are applied server-side; the page is what the user sees.
 */
export const useDevicesList = ({ refreshTrigger, initialDeviceTypeFilter, initialStatusFilter, initialClientFilter }: DevicesListOptions) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  // ANEP staff work across organisations; a client's own staff only ever
  // see their own devices, so a "filter by client" control offers them a
  // choice they do not have.
  const worksAcrossClients = isSuperAdmin || isAdmin;
  const canSeeInactive = isAdmin || isSuperAdmin;

  const [devices, setDevices] = useState<DeviceCreate[]>([]);
  const [pagination, setPagination] = useState<DevicePage['pagination']>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterClient, setFilterClient] = useState<string[]>(initialClientFilter ?? []);
  const [filterDeviceType, setFilterDeviceType] = useState<string[]>(initialDeviceTypeFilter ? [initialDeviceTypeFilter] : []);
  const [filterTemplate, setFilterTemplate] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>(initialStatusFilter === 'active' || initialStatusFilter === 'refresh' ? initialStatusFilter : '');
  // Soft-delete: admins can opt in to see deactivated devices.
  const [showInactive, setShowInactive] = useState(false);

  // Sort and page
  const [sortField, setSortField] = useState<DeviceSortField>('idDevice');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Client names for the table and the filter
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [clientList, setClientList] = useState<ClientOption[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const filters = useMemo<DeviceFilters>(() => ({
    clients: filterClient,
    deviceTypes: filterDeviceType,
    templates: filterTemplate,
    status: filterStatus,
    search: debouncedSearch,
    includeInactive: showInactive && canSeeInactive,
  }), [filterClient, filterDeviceType, filterTemplate, filterStatus, debouncedSearch, showInactive, canSeeInactive]);

  const loadPage = useCallback(async (signal?: AbortSignal) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const page = await fetchDevicePage(filters, { page: currentPage, pageSize: itemsPerPage, sortField, sortDirection }, signal);
      setDevices(page.items);
      setPagination(page.pagination);
    } catch (err: any) {
      if (isAbortError(err)) return;
      setError(err.message || 'Failed to fetch devices');
      console.error('Error fetching devices:', err);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [isAuthenticated, filters, currentPage, itemsPerPage, sortField, sortDirection]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const ac = new AbortController();
    loadPage(ac.signal);
    return () => ac.abort();
  }, [loadPage, isAuthenticated, refreshTrigger]);

  // Any filter, search or sort change starts again at page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [filterClient, filterDeviceType, filterTemplate, filterStatus, debouncedSearch, sortField, sortDirection]);

  useEffect(() => {
    if (initialDeviceTypeFilter) setFilterDeviceType([initialDeviceTypeFilter]);
  }, [initialDeviceTypeFilter]);

  // clientService is process-cached and dedups in flight, so no signal:
  // the result is dropped if the effect has been cleaned up meanwhile.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    clientService.fetchAllClients()
      .then((clients) => {
        if (cancelled) return;
        const names = new Map<string, string>();
        const list: ClientOption[] = [];
        const byId = indexClients(clients);
        clients.forEach((client) => {
          // An agency reads "root › agency", so two "Lyon" agencies of different groups stay apart.
          const label = clientPathLabel(client, byId);
          names.set(client.id, label);
          list.push({ id: client.id, name: label });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setClientNames(names);
        setClientList(list);
      })
      .catch((err) => { if (!cancelled) console.error('Failed to fetch client names:', err); });
    return () => { cancelled = true; };
  }, [isAuthenticated, refreshTrigger]);

  // Rows without an identifier are skipped defensively.
  const rows = useMemo(() => devices.filter((device: any) => {
    const id = device.idDevice || device.id_device || device.id;
    if (!device || !id) {
      console.warn('Skipping device with missing deviceId:', device);
      return false;
    }
    return true;
  }), [devices]);

  const serverPaged = pagination !== null;
  const totalPages = serverPaged ? pagination!.total_pages : Math.ceil(rows.length / itemsPerPage);
  const totalItems = serverPaged ? pagination!.total_items : rows.length;
  const pageRows = useMemo(() => {
    if (serverPaged) return rows;
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, serverPaged, currentPage, itemsPerPage]);

  const clientName = (clientId?: string | null): string =>
    (clientId && (clientNames.get(clientId) || clientNames.get(clientId.replace(/-/g, '')))) || '-';

  const sort = (field: DeviceSortField) => {
    if (field === sortField) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const changePageSize = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  };

  /** A device edited in its dialog replaces its row without a refetch. */
  const replaceDevice = (updated: DeviceCreate) => {
    setDevices((prev) => prev.map((device) => (device.idDevice === updated.idDevice ? updated : device)));
  };

  return {
    // access
    worksAcrossClients, canSeeInactive,
    // data
    loading, error, pageRows, totalPages, totalItems, clientNames, clientList, clientName, replaceDevice, reload: () => loadPage(),
    // filters
    filters, searchTerm, setSearchTerm, filterClient, setFilterClient, filterDeviceType, setFilterDeviceType,
    filterTemplate, setFilterTemplate, filterStatus, setFilterStatus, showInactive, setShowInactive,
    // sort and page
    sortField, sortDirection, sort, currentPage, setCurrentPage, itemsPerPage, changePageSize,
  };
};

export type DevicesListState = ReturnType<typeof useDevicesList>;
